import { useCallback, useEffect, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getDashboardBranchPerformance } from '@/api/dashboard'
import { PnLMetricCardModel } from '@/types/clarity'
import { DashboardBranchPerformanceItemModel } from '@/types/dashboard'
import { dateToString } from '@/utils'

function mapToMetricCardModel(
  item: DashboardBranchPerformanceItemModel
): PnLMetricCardModel {
  return {
    storeProfits: {
      value: item.storeProfits,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.storeProfits,
    },
    netSales: {
      value: item.sales ?? 0,
      mtd: 0,
      ytd: 0,
      metricType: 'money',
      amount: item.sales ?? 0,
    },
    cogs: {
      value: item.cogs,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.cogs,
    },
    storeLabor: {
      value: item.storeLabor,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.storeLabor,
    },
    opEx: {
      value: item.opex,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.opex,
    },
    rentAndOccupancy: {
      value: item.occupancy,
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
      amount: item.occupancy,
    },
  }
}

function mapToTrendCardModel(
  item: DashboardBranchPerformanceItemModel
): PnLMetricCardModel {
  return {
    storeProfits: {
      value: item.storeProfits - (item.storeProfitsChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
    },
    netSales: {
      value: (item.sales ?? 0) - (item.salesChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'money',
    },
    cogs: {
      value: item.cogs - (item.cogsChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
    },
    storeLabor: {
      value: item.storeLabor - (item.storeLaborChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
    },
    opEx: {
      value: item.opex - (item.opexChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
    },
    rentAndOccupancy: {
      value: item.occupancy - (item.occupancyChange ?? 0),
      mtd: 0,
      ytd: 0,
      metricType: 'percentage',
    },
  }
}

export default function useDashboardBranchPerformance(
  branches: string,
  date: DateRange | undefined
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchPerformanceChartData, setBranchPerformanceChartData] = useState<
    DashboardBranchPerformanceItemModel[]
  >([])
  const [systemTotalPerformance, setSystemTotalPerformance] = useState<
    DashboardBranchPerformanceItemModel | undefined
  >()
  const [metricCardRecord, setMetricCardRecord] = useState<
    Record<string, PnLMetricCardModel>
  >({})
  const [miniCardData, setMiniCardData] = useState<
    PnLMetricCardModel | undefined
  >()
  const [trendData, setTrendData] = useState<PnLMetricCardModel | undefined>()

  const fetchAllData = useCallback(async () => {
    if (!branches || !date?.from || !date?.to) {
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const startMonth = dateToString(date, 'from', 'yyyy-mm')
      const endMonth = dateToString(date, 'to', 'yyyy-mm')

      const { branch_data: branchData } = await getDashboardBranchPerformance(
        branches,
        startMonth,
        endMonth
      )

      const data = branchData ?? []

      const systemTotalItem = data.find(
        (item) => item.branch_name === 'system_total'
      )

      const branchItems = data.filter(
        (item) => item.branch_name !== 'system_total'
      )

      const mapItemToModel = (
        item: (typeof data)[number]
      ): DashboardBranchPerformanceItemModel => ({
        branchName: item.branch_name,
        value: item.value,
        storeProfits: item.store_profits,
        cogs: item.cogs,
        storeLabor: item.store_labor,
        opex: item.opex,
        occupancy: item.occupancy,
        sales: item.sales,
        salesChange: item.sales_change,
        valueChange: item.value_change,
        storeProfitsChange: item.store_profits_change,
        cogsChange: item.cogs_change,
        storeLaborChange: item.store_labor_change,
        opexChange: item.opex_change,
        occupancyChange: item.occupancy_change,
      })

      const mappedBranches = branchItems.map(mapItemToModel)

      setBranchPerformanceChartData(mappedBranches)
      setSystemTotalPerformance(
        systemTotalItem ? mapItemToModel(systemTotalItem) : undefined
      )

      // Build metricCardRecord: one entry per branch (x-axis = branch name)
      const record: Record<string, PnLMetricCardModel> = {}
      for (const branch of mappedBranches) {
        record[branch.branchName] = mapToMetricCardModel(branch)
      }
      setMetricCardRecord(record)

      // Build miniCardData & trendData from system total
      if (systemTotalItem) {
        const systemModel = mapItemToModel(systemTotalItem)
        setMiniCardData(mapToMetricCardModel(systemModel))
        setTrendData(mapToTrendCardModel(systemModel))
      } else {
        setMiniCardData(undefined)
        setTrendData(undefined)
      }
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [branches, date])

  const refetch = useCallback(() => {
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    branchPerformanceChartData,
    systemTotalPerformance,
    metricCardRecord,
    miniCardData,
    trendData,
    loading,
    error,
    refetch,
  }
}
